import {EventEmitter, Injectable, Output} from '@angular/core';
import { Canvas, Rect, Line } from 'fabric';
import { LoggerService } from '../handlers/logger.service';
import {TesseractOutput} from "../pages/invoice/invoice-jobs/line-item-job/line-item-job.component";

export interface GridCell {
  row: number;
  col: number;
  text: string;
  confidence?: number;
  hasLowConfidence?: boolean;
  ignored: boolean;
  bounds: { left: number; top: number; width: number; height: number };
  location?: GridHighlightLocation;
}

export interface GridData {
  rows: number;
  cols: number;
  cells: GridCell[];
}

export interface GridHighlightLocation {
  pageKey: string;
  row: number;
  col: number;
}

export interface TableDetectionResult {
  found: boolean;
  bounds: { left: number; top: number; width: number; height: number } | null;
  rows: number;
  cols: number;
  confidence: number
  textItems?: any[];
  startY?: number;
  detectionStrategy?: 'keyword' | 'pattern';
  columnBoundaries?: number[];
}

interface SerializedGridLine {
  type: 'horizontal' | 'vertical';
  ratio: number; // Stored relative to the enclosing grid rectangle
}

interface PageGridState {
  rect: {
    leftRatio: number;
    topRatio: number;
    widthRatio: number;
    heightRatio: number;
  };
  lines: SerializedGridLine[];
  rowCount: number;
  colCount: number;
  highlight?: GridHighlightLocation | null;
}

type GridHandleType = 'left' | 'right' | 'top' | 'bottom';
type FabricMouseEventName = 'mouse:down' | 'mouse:move' | 'mouse:up';

interface CanvasDrawState {
  isDrawing: boolean;
  startX: number;
  startY: number;
}

interface NormalizedOcrItem {
  text: string;
  xRatio: number;
  yRatio: number;
  widthRatio: number;
  heightRatio: number;
  confidence?: number;
}

interface CachedOcrPage {
  items: NormalizedOcrItem[];
}

interface ColumnTemplateSnapshot {
  columnRatios: number[];
  columnMappings: { [key: number]: string | null };
  columnCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class GridExtractionService {

  @Output() cellClick = new EventEmitter<{ cell: GridCell; location: GridHighlightLocation }>();
  @Output() gridRectCompleted = new EventEmitter<void>();

  private fabricCanvas: Canvas | null = null;
  private gridRect: Rect | null = null;
  private gridLines: Line[] = [];
  private rowCount = 1;
  private colCount = 1;
  private pageOcrCache = new Map<string, CachedOcrPage>(); // Key: pageKey, Value: normalized OCR items
  private pageGridStates = new Map<string, PageGridState>();
  private tesseractCache: TesseractOutput | null = null;
  private activePageKey: string | null = null;
  private activeHighlight: GridHighlightLocation | null = null;
  private gridEdgeHandles: Partial<Record<GridHandleType, Rect>> = {};
  private fabricCanvasHandlers: Partial<Record<FabricMouseEventName, (options: any) => void>> = {};
  private readonly GRID_HANDLE_THICKNESS = 24;
  private readonly GRID_MIN_WIDTH = 40;
  private readonly GRID_MIN_HEIGHT = 40;
  private readonly LOW_CONFIDENCE_THRESHOLD = 80;
  private readonly CELL_ITEM_OVERLAP_THRESHOLD = 0.5;
  private readonly MIN_VERTICAL_AXIS_GAP = 4;
  private readonly MIN_HORIZONTAL_AXIS_GAP = 1;
  private savedColumnTemplate: ColumnTemplateSnapshot | null = null;

  // Header keywords used for table/grid header detection across all methods
  protected readonly HEADER_KEYWORDS = [
    'quantity', 'qty', 'q\'ty', 'qnty',
    'description', 'desc', 'item',
    'unit price', 'price', 'rate', 'amount',
    'total', 'subtotal', 'sum',
    'product', 'gross total', 'part',
    'code', 'gst amount', 'gst inclusive', 'uom', 'u/m',
    'supplier ref', 'supplier reference', 'plu', 'order'
  ];

  constructor(private logger: LoggerService) {}

  getAutoColumnMappings(
    _columnSummaries: Array<{ index: number; header: string; dataCharacteristic?: string; averageCurrencyValue?: number | null }>,
    _lineItemColumns: Array<{ field: string; header: string }>
  ): { [key: number]: string } | null {
    return null;
  }

  getHeaderKeywords(): readonly string[] {
    return this.HEADER_KEYWORDS;
  }

  getCurrentColumnRatios(): number[] {
    const verticalLines = this.gridLines.filter(line => (line as any).data?.type === 'vertical');
    if (!verticalLines.length) {
      return [];
    }

    const bounds = this.getScaledGridBounds();
    return verticalLines
      .map(line => {
        const ratio = (line as any).data?.ratioWithinRect;
        if (typeof ratio === 'number') {
          return ratio;
        }
        if (!bounds) {
          return 0;
        }
        const position = line.left || 0;
        return this.computeRatioWithinBounds('vertical', position, bounds);
      })
      .sort((a, b) => a - b);
  }

  saveColumnTemplateSnapshot(
    columnRatios: number[],
    columnMappings: { [key: number]: string | null },
    columnCount: number
  ): void {
    this.savedColumnTemplate = {
      columnRatios: [...columnRatios],
      columnMappings: { ...columnMappings },
      columnCount
    };
  }

  getColumnTemplateSnapshot(): ColumnTemplateSnapshot | null {
    if (!this.savedColumnTemplate) {
      return null;
    }

    return {
      columnRatios: [...this.savedColumnTemplate.columnRatios],
      columnMappings: { ...this.savedColumnTemplate.columnMappings },
      columnCount: this.savedColumnTemplate.columnCount
    };
  }

  clearColumnTemplateSnapshot(): void {
    this.savedColumnTemplate = null;
  }

  tryApplySavedColumnTemplateToCurrentGrid(): boolean {
    if (!this.savedColumnTemplate || !this.gridRect) {
      return false;
    }

    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      return false;
    }

    const verticalLineCount = this.savedColumnTemplate.columnRatios.length;
    if (this.savedColumnTemplate.columnCount > 1 && verticalLineCount === 0) {
      return false;
    }

    this.removeVerticalGridLines();
    this.savedColumnTemplate.columnRatios.forEach((ratio, index) => {
      const x = bounds.left + ratio * bounds.width;
      const line = this.createGridLine('vertical', x, index + 1);
      this.registerLine(line, { ratioOverride: ratio });
    });

    this.colCount = this.savedColumnTemplate.columnCount;
    this.enableCustomGridResizeMode();
    this.fabricCanvas?.renderAll();
    return true;
  }

  private removeVerticalGridLines(): void {
    if (!this.fabricCanvas || !this.gridLines.length) {
      return;
    }

    this.gridLines = this.gridLines.filter(line => {
      const type = (line as any).data?.type;
      if (type === 'vertical') {
        this.fabricCanvas!.remove(line);
        return false;
      }
      return true;
    });
  }


  // ========== Fabric Canvas setup

  initializeFabricCanvas(canvasElement: HTMLCanvasElement, width: number, height: number, pageKey: string): void {
    if (this.fabricCanvas) {
      this.detachFabricCanvasEvents();
      this.fabricCanvas.dispose();
    }

    // Add Fabric.js on the canvas - adds a wrapper div over the existing canvas
    this.fabricCanvas = new Canvas(canvasElement, {
      width,
      height,
      selection: true,
      stopContextMenu: true,
      fireRightClick: true
    });

    this.activePageKey = pageKey;
    this.restoreActivePageState();
  }

  private attachFabricCanvasEvents(): void {
    if (!this.fabricCanvas) {
      this.logger.error('Cannot attach Fabric handlers: fabric canvas not initialized');
      return;
    }

    const canvas = this.fabricCanvas;
    const drawState: CanvasDrawState = { isDrawing: false, startX: 0, startY: 0 };

    // detaching via canvas.off() simplify the code, but that method is deprecated
    this.detachFabricCanvasEvents();

    const mouseDownHandler = (options: any) => this.handleCanvasMouseDown(canvas, options, drawState);
    const mouseMoveHandler = (options: any) => this.handleCanvasMouseMove(canvas, options, drawState);
    const mouseUpHandler = () => this.handleCanvasMouseUp(drawState);

    canvas.on('mouse:down', mouseDownHandler);
    canvas.on('mouse:move', mouseMoveHandler);
    canvas.on('mouse:up', mouseUpHandler);

    this.fabricCanvasHandlers = {
      'mouse:down': mouseDownHandler,
      'mouse:move': mouseMoveHandler,
      'mouse:up': mouseUpHandler
    };
  }

  private detachFabricCanvasEvents(): void {
    if (!this.fabricCanvas) {
      this.fabricCanvasHandlers = {};
      return;
    }

    (Object.entries(this.fabricCanvasHandlers) as Array<[FabricMouseEventName, (options: any) => void]>).forEach(
      ([eventName, handler]) => {
        if (handler) {
          this.fabricCanvas!.off(eventName, handler);
        }
      }
    );

    this.fabricCanvasHandlers = {};
  }

  // =============== Grid state saving and restoration

  synchronizePageStateWithCanvas(pageKey: string, width: number, height: number): void {
    this.saveActivePageState();    // persist previous page before switching
    this.activePageKey = pageKey;

    const existingCanvas = this.fabricCanvas;
    if (!existingCanvas) {
      this.logger.warn('[Fabric] Cannot synchronize: canvas not initialized');
      return;
    }

    // Fabric must be recreated to update width/height, so dispose and rebuild once
    this.detachFabricCanvasEvents();
    const canvasElement = existingCanvas.getElement();
    existingCanvas.dispose();
    this.fabricCanvas = null;

    this.fabricCanvas = new Canvas(canvasElement, {
      width,
      height,
      selection: true,
      stopContextMenu: true,
      fireRightClick: true
    });

    this.restoreActivePageState();
  }

  private saveActivePageState(): void {
    if (!this.activePageKey) return;

    if (!this.fabricCanvas || !this.gridRect) {
      this.pageGridStates.delete(this.activePageKey);
      return;
    }

    const canvasWidth = this.fabricCanvas.getWidth();
    const canvasHeight = this.fabricCanvas.getHeight();
    const rectScaleX = this.gridRect.scaleX || 1;
    const rectScaleY = this.gridRect.scaleY || 1;

    const highlightForPage =
      this.activePageKey && this.activeHighlight && this.activeHighlight.pageKey === this.activePageKey
        ? { ...this.activeHighlight }
        : null;

    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      this.logger.warn('[Grid State] Unable to compute scaled bounds while saving page state.');
      return;
    }

    this.pageGridStates.set(this.activePageKey, {
      rect: {
        leftRatio: (this.gridRect.left || 0) / canvasWidth,
        topRatio: (this.gridRect.top || 0) / canvasHeight,
        widthRatio: ((this.gridRect.width || 0) * rectScaleX) / canvasWidth,
        heightRatio: ((this.gridRect.height || 0) * rectScaleY) / canvasHeight
      },
      lines: this.gridLines.map((line, index) => {
        const lineData = (line as any).data;
        const isHorizontal = lineData?.type === 'horizontal';
        const absolutePosition = isHorizontal ? (line.top || 0) : (line.left || 0);
        const storedRatio = typeof lineData?.ratioWithinRect === 'number' ? lineData.ratioWithinRect : undefined;
        const ratio = typeof storedRatio === 'number'
          ? storedRatio
          : this.computeRatioWithinBounds(isHorizontal ? 'horizontal' : 'vertical', absolutePosition, bounds);

        return {
          type: isHorizontal ? 'horizontal' : 'vertical',
          ratio
        };
      }),
      rowCount: this.rowCount,
      colCount: this.colCount,
      highlight: highlightForPage
    });
  }

  private restoreActivePageState(): void {
    this.clearCanvasObjects();

    if (!this.fabricCanvas || !this.activePageKey) {
      return;
    }

    const state = this.pageGridStates.get(this.activePageKey);
    if (!state) {
      this.fabricCanvas.renderAll();
      this.attachFabricCanvasEvents();
      return;
    }

    const canvasWidth = this.fabricCanvas.getWidth();
    const canvasHeight = this.fabricCanvas.getHeight();

    this.rowCount = state.rowCount;
    this.colCount = state.colCount;
    this.activeHighlight = state.highlight ? { ...state.highlight } : null;
    this.gridRect = new Rect({
      left: state.rect.leftRatio * canvasWidth,
      top: state.rect.topRatio * canvasHeight,
      width: state.rect.widthRatio * canvasWidth,
      height: state.rect.heightRatio * canvasHeight,
      fill: 'rgba(0, 255, 0, 0.2)',
      stroke: '#007bff',
      strokeWidth: 4,
      selectable: false,
      evented: false,
      lockRotation: true,
      lockMovementX: true,
      lockMovementY: true,
      lockScalingX: true,
      lockScalingY: true,
      hasControls: false,
      hasBorders: true,
      borderColor: '#007bff',
      cornerColor: '#007bff',
      cornerSize: 12,
      transparentCorners: false,
      cornerStyle: 'circle'
    });
    this.fabricCanvas.add(this.gridRect);

    this.gridLines = [];
    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      this.logger.warn('[Grid State] Unable to compute bounds while restoring lines.');
    } else {
      state.lines.forEach((lineState, index) => {
        const isHorizontal = lineState.type === 'horizontal';
        const rectStart = isHorizontal ? bounds.top : bounds.left;
        const rectSize = isHorizontal ? bounds.height : bounds.width;
        const ratio = typeof lineState.ratio === 'number' ? lineState.ratio : 0;
        const linePosition = rectStart + ratio * rectSize;
        const line = this.createGridLine(lineState.type, linePosition, index + 1);

        this.registerLine(line, { ratioOverride: ratio });
      });
    }

    this.enableCustomGridResizeMode();
    this.fabricCanvas.renderAll();
    this.attachFabricCanvasEvents();
    this.updateActivePageHighlightState();
  }

  private createGridLine(type: 'horizontal' | 'vertical', position: number, index: number): Line {
    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      throw new Error('Cannot create grid line: missing grid bounds');
    }
    const { left, top, width, height } = bounds;
    const isHorizontal = type === 'horizontal';

    const line = new Line(
      isHorizontal ? [left, position, left + width, position] : [position, top, position, top + height],
      {
        stroke: '#007bff',
        strokeWidth: 4,
        selectable: true,
        lockMovementX: isHorizontal,
        lockMovementY: !isHorizontal,
        lockRotation: true,
        lockScalingX: true,
        lockScalingY: true,
        hasControls: false,
        hasBorders: false,
        hoverCursor: isHorizontal ? 'ns-resize' : 'ew-resize',
        moveCursor: isHorizontal ? 'ns-resize' : 'ew-resize',
        evented: true,
        perPixelTargetFind: true,
        targetFindTolerance: 8,
        objectCaching: false,
        data: { type, index }
      } as any
    );

    this.attachLineMoveHandler(line, type);
    this.attachLineDeleteHandler(line);
    return line;
  }

  // ========== Grid drawing mouse events on fabric canvas

  private handleCanvasMouseDown(canvas: Canvas, options: any, drawState: CanvasDrawState): void {
    if (!options.e) {
      return;
    }

    const mouseEvent = options.e as MouseEvent;
    // right-click to delete line
    if (mouseEvent.button === 2) {
      mouseEvent.preventDefault();
      mouseEvent.stopPropagation();

      const target = options.target as Line | undefined;
      if (target && (target as any).data?.type) {
        this.deleteLine(target);
        canvas.requestRenderAll();
      }
      return;
    }

    // left clicks on targets are ignored
    if (options.target || mouseEvent.button !== 0) {
      return;
    }

    // Shift/Ctrl + left Click to add horizontal/vertical lines
    if (this.gridRect && !drawState.isDrawing && (mouseEvent.shiftKey || mouseEvent.ctrlKey || mouseEvent.metaKey)) {
      const pointer = canvas.getScenePoint(mouseEvent);
      if (this.isPointInsideGrid(pointer.x, pointer.y)) {
        if (mouseEvent.shiftKey) {
          this.addHorizontalLineAtPosition(pointer.y);
        }
        if (mouseEvent.ctrlKey || mouseEvent.metaKey) {
          this.addVerticalLineAtPosition(pointer.x);
        }
      }
      return;
    }

    // start drawing the table enclosing box if it doesn't exist
    if (!this.gridRect) {
      this.startGridDrawing(canvas, mouseEvent, drawState);
    }
  }

  private deleteLine(line: Line): void {
    if (!this.fabricCanvas) return;

    const lineData = (line as any).data;

    if (!lineData) {
      return;
    }

    // Remove the line from canvas
    this.fabricCanvas.remove(line);
    // Remove from gridLines array
    const index = this.gridLines.indexOf(line);
    if (index > -1) {
      this.gridLines.splice(index, 1);
    }

    // Update row/column count
    if (lineData.type === 'horizontal') {
      this.rowCount--;
    } else if (lineData.type === 'vertical') {
      this.colCount--;
    }

    this.fabricCanvas.renderAll();
  }

  private isPointInsideGrid(x: number, y: number): boolean {
    const bounds = this.getScaledGridBounds();
    if (!bounds) return false;

    const { left, top, width, height } = bounds;

    return x >= left && x <= left + width && y >= top && y <= top + height;
  }

  private addHorizontalLineAtPosition(y: number): void {
    if (!this.requireGridContext('add horizontal line') || !this.fabricCanvas) {
      return;
    }

    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      return;
    }

    const { top, height } = bounds;

    // Ensure y is within SCALED grid bounds
    if (y < top || y > top + height) {
      this.logger.warn(`Cannot add horizontal line: position ${y} outside scaled grid bounds [${top}, ${top + height}]`);
      return;
    }

    const line = this.createGridLine('horizontal', y, this.rowCount);
    this.registerLine(line, { trackCounts: true });
    this.fabricCanvas.renderAll();
  }

  private addVerticalLineAtPosition(x: number): void {
    if (!this.requireGridContext('add vertical line') || !this.fabricCanvas) {
      return;
    }

    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      return;
    }

    const { left, width } = bounds;

    // Ensure x is within SCALED grid bounds
    if (x < left || x > left + width) {
      this.logger.warn(`Cannot add vertical line: position ${x} outside scaled grid bounds [${left}, ${left + width}]`);
      return;
    }

    const line = this.createGridLine('vertical', x, this.colCount);
    this.registerLine(line, { trackCounts: true });
    this.fabricCanvas.renderAll();
  }

  private startGridDrawing(canvas: Canvas, mouseEvent: MouseEvent, drawState: CanvasDrawState): void {
    drawState.isDrawing = true;
    const pointer = canvas.getScenePoint(mouseEvent);
    drawState.startX = pointer.x;
    drawState.startY = pointer.y;

    this.gridRect = new Rect({
      left: drawState.startX,
      top: drawState.startY,
      width: 0,
      height: 0,
      fill: 'rgba(0, 255, 0, 0.2)',
      stroke: '#007bff',
      strokeWidth: 4,
      selectable: true,
      evented: true
    });

    canvas.add(this.gridRect);
    canvas.renderAll();
  }

  private handleCanvasMouseMove(canvas: Canvas, options: any, drawState: CanvasDrawState): void {
    if (!drawState.isDrawing || !this.gridRect || !options.e) {
      return;
    }

    const pointer = canvas.getScenePoint(options.e);
    this.resizeDraftGridRect(pointer.x, pointer.y, drawState);
    canvas.renderAll();
  }

  private resizeDraftGridRect(pointerX: number, pointerY: number, drawState: CanvasDrawState): void {
    if (!this.gridRect) return;

    const width = pointerX - drawState.startX;
    const height = pointerY - drawState.startY;

    if (width > 0) {
      this.gridRect.set({ width });
    } else {
      this.gridRect.set({ left: pointerX, width: drawState.startX - pointerX });
    }

    if (height > 0) {
      this.gridRect.set({ height });
    } else {
      this.gridRect.set({ top: pointerY, height: drawState.startY - pointerY });
    }
  }

  private handleCanvasMouseUp(drawState: CanvasDrawState): void {
    if (!drawState.isDrawing || !this.gridRect) {
      return;
    }

    drawState.isDrawing = false;
    this.finalizeGridRect();
  }

  private finalizeGridRect(): void {
    if (!this.gridRect) {
      return;
    }

    this.gridRect.set({
      selectable: false,
      evented: false,
      lockRotation: true,
      lockMovementX: true,
      lockMovementY: true,
      lockScalingX: true,
      lockScalingY: true,
      hasControls: false,
      hasBorders: true,
      borderColor: '#007bff',
      cornerColor: '#007bff',
      cornerSize: 12,
      transparentCorners: false,
      cornerStyle: 'circle'
    });
    this.gridRect.setCoords();
    this.gridRectCompleted.emit();
  }

  // ===================== Auto grid detection

  async autoDetectGridLinesFromSelection(
    pdfCanvas: HTMLCanvasElement,
    pageNumber: number,
    pageKey?: string
  ): Promise<boolean> {
    if (!this.gridRect || !this.fabricCanvas) {
      throw new Error('No grid defined');
    }

    const textItems = this.getTextItemsForPage(pdfCanvas, pageNumber, pageKey, 'Auto Detect Grid');
    if (textItems.length === 0) {
      this.logger.warn('[Auto Detect Grid] No text detected in selected area');
      return false;
    }

    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      throw new Error('Cannot auto detect grid lines: missing grid bounds');
    }

    const scaleX_Internal = pdfCanvas.width / this.fabricCanvas.getWidth();
    const scaleY_Internal = pdfCanvas.height / this.fabricCanvas.getHeight();

    return this.autoGenerateGridStructure(textItems, bounds, scaleX_Internal, scaleY_Internal);
  }

  private autoGenerateGridStructure(
    textItems: any[],
    bounds: { left: number; top: number; width: number; height: number },
    scaleX_Internal: number,
    scaleY_Internal: number
  ): boolean {
    if (this.gridLines.length !== 0 || textItems.length === 0) {
      return false;
    }

    this.logger.debug('[Auto-Grid] No lines found. Detecting structure from JSON...');
    const wordsInBox = textItems.filter(item =>
      item.x >= (bounds.left * scaleX_Internal) && item.x <= ((bounds.left + bounds.width) * scaleX_Internal) &&
      item.y >= (bounds.top * scaleY_Internal) && item.y <= ((bounds.top + bounds.height) * scaleY_Internal)
    );

    if (wordsInBox.length === 0) {
      this.logger.warn('[Auto-Grid] No OCR words detected inside drawn area');
      return false;
    }

    const gapBoundaries = this.calculateGridBoundariesFromWordGaps(wordsInBox, bounds);

    if (gapBoundaries) {
      const rowCount = gapBoundaries.rowBoundaries.length - 1;
      const colCount = gapBoundaries.columnBoundaries.length - 1;

      this.logger.debug(`[Auto-Grid] Gap-based detection found ${rowCount} rows and ${colCount} columns`);

      this.createGridFromBoundsWithContentAdjustment(
        bounds,
        rowCount,
        colCount,
        gapBoundaries.columnBoundaries,
        gapBoundaries.rowBoundaries
      );
      return true;
    }

    this.logger.warn('[Auto-Grid] Gap-based detection failed to determine table structure');
    return false;
  }

  private calculateGridBoundariesFromWordGaps(
    words: any[],
    bounds: { left: number; top: number; width: number; height: number }
  ): { columnBoundaries: number[]; rowBoundaries: number[] } | null {
    if (!words || words.length === 0) {
      return null;
    }

    const right = bounds.left + bounds.width;
    const bottom = bounds.top + bounds.height;

    const columnCenters = this.findAxisGapCenters(words, 'x', bounds.left, right);
    const rowCenters = this.findAxisGapCenters(words, 'y', bounds.top, bottom);

    const columnBoundaries = [bounds.left, ...columnCenters, right];
    const rowBoundaries = [bounds.top, ...rowCenters, bottom];

    return { columnBoundaries, rowBoundaries };
  }

  private findAxisGapCenters(
    words: any[],
    axis: 'x' | 'y',
    startBound: number,
    endBound: number
  ): number[] {
    if (startBound >= endBound) {
      return [];
    }

    const intervals = words.map(word => {
      const start = axis === 'x' ? word.x : word.y;
      const size = axis === 'x' ? word.width : word.height;
      const end = start + size;

      const clampedStart = Math.max(startBound, start);
      const clampedEnd = Math.min(endBound, end);

      return clampedEnd > clampedStart ? { start: clampedStart, end: clampedEnd } : null;
    }).filter((segment): segment is { start: number; end: number } => segment !== null);

    if (intervals.length === 0) {
      return [];
    }

    intervals.sort((a, b) => a.start - b.start);

    const merged: { start: number; end: number }[] = [];
    intervals.forEach(segment => {
      const last = merged[merged.length - 1];
      if (last && segment.start <= last.end) {
        last.end = Math.max(last.end, segment.end);
      } else {
        merged.push({ start: segment.start, end: segment.end });
      }
    });

    if (merged.length < 2) {
      return [];
    }

    const centers: number[] = [];
    let previousEnd = merged[0].end;

    for (let i = 1; i < merged.length; i++) {
      const gapStart = previousEnd;
      const gapEnd = merged[i].start;
      const gapSize = gapEnd - gapStart;

      const minGap = axis === 'x' ? this.MIN_VERTICAL_AXIS_GAP : this.MIN_HORIZONTAL_AXIS_GAP;
      if (gapSize >= minGap) {
        centers.push(gapStart + gapSize / 2);
      }

      previousEnd = Math.max(previousEnd, merged[i].end);
    }

    return centers;
  }

  createGridFromBoundsWithContentAdjustment(
    bounds: { left: number; top: number; width: number; height: number },
    rows: number,
    cols: number,
    columnBoundaries: number[],
    rowBoundaries: number[]
  ): void {
    if (!this.fabricCanvas) {
      this.logger.error('[Auto Grid] Cannot create grid: fabric canvas not initialized');
      return;
    }

    if (!columnBoundaries || columnBoundaries.length < 2) {
      this.logger.error('[Auto Grid] Invalid column boundaries provided');
      return;
    }

    if (!rowBoundaries || rowBoundaries.length < 2) {
      this.logger.error('[Auto Grid] Invalid row boundaries provided');
      return;
    }

    this.clearGrid();
    this.rowCount = rows;
    this.colCount = cols;

    this.gridRect = new Rect({
      left: bounds.left,
      top: bounds.top,
      width: bounds.width,
      height: bounds.height,
      fill: 'rgba(0, 255, 0, 0.2)',
      stroke: '#007bff',
      strokeWidth: 4,
      selectable: false,
      evented: false,
      lockRotation: true,
      lockScalingFlip: true,
      lockScalingX: true,
      lockScalingY: true,
      lockMovementX: true,  // DISABLE MOVING
      lockMovementY: true,  // DISABLE MOVING
      hasControls: false,
      hasBorders: true,
      borderColor: '#007bff',
      cornerColor: '#007bff',
      cornerSize: 12,
      transparentCorners: false,
      cornerStyle: 'circle',
      objectCaching: false
    });

    this.gridRect.on('moving', () => {
      this.updateGridLinesPosition();
    });

    this.gridRect.on('scaling', () => {
      this.updateGridLinesPosition();
    });

    this.gridRect.on('modified', () => {
      this.updateGridLinesPosition();
    });

    // Prevent grid lines from resetting on selection
    this.gridRect.on('selected', () => {
      this.logger.debug('[Grid] Grid rectangle selected');
    });

    this.fabricCanvas.add(this.gridRect);

    this.drawGridLinesWithAdjustedColumnsAndRows(columnBoundaries, rowBoundaries);
  }

  private drawGridLinesWithAdjustedColumnsAndRows(columnBoundaries: number[], rowBoundaries: number[]): void {
    if (!this.requireGridContext('draw content-adjusted grid lines')) return;

    // Only clear if we're creating new lines, not on selection
    if (this.gridLines.length > 0) {
      this.gridLines.forEach(line => this.fabricCanvas!.remove(line));
      this.gridLines = [];
    }

    // Draw horizontal lines (rows) - using content-adjusted row boundaries
    for (let i = 1; i < rowBoundaries.length - 1; i++) {
      const y = rowBoundaries[i];
      const line = this.createGridLine('horizontal', y, i);
      this.registerLine(line);
    }

    // Draw vertical lines (columns) - using adjusted boundaries
    for (let i = 1; i < columnBoundaries.length - 1; i++) {
      const x = columnBoundaries[i];
      const line = this.createGridLine('vertical', x, i);
      this.registerLine(line);
    }

    this.enableCustomGridResizeMode();
    this.fabricCanvas.renderAll();
  }

  // ===================== Grid line event handlers

  private attachLineMoveHandler(line: Line, type: 'horizontal' | 'vertical'): void {
    line.on('moving', () => {
      const bounds = this.getScaledGridBounds();
      if (!bounds) {
        return;
      }

      if (type === 'horizontal') {
        const minY = bounds.top + 10;
        const maxY = bounds.top + bounds.height - 10;
        if (line.top! < minY) line.top = minY;
        if (line.top! > maxY) line.top = maxY;
      } else {
        const minX = bounds.left + 10;
        const maxX = bounds.left + bounds.width - 10;
        if (line.left! < minX) line.left = minX;
        if (line.left! > maxX) line.left = maxX;
      }

      this.updateLineRatioFromPosition(line, bounds);
    });
  }

  private attachLineDeleteHandler(line: Line): void {
    line.on('mousedown', (event) => {
      const mouseEvent = event.e as MouseEvent | undefined;
      if (!mouseEvent || mouseEvent.button !== 2) {
        return;
      }

      mouseEvent.preventDefault();
      mouseEvent.stopPropagation();
      this.deleteLine(line);
    });
  }

  private registerLine(line: Line, options?: { trackCounts?: boolean; ratioOverride?: number }): Line {
    if (!this.fabricCanvas) {
      return line;
    }

    this.gridLines.push(line);
    this.fabricCanvas.add(line);
    if (typeof options?.ratioOverride === 'number') {
      this.applyLineRatio(line, options.ratioOverride);
    } else {
      const bounds = this.getScaledGridBounds();
      if (bounds) {
        this.updateLineRatioFromPosition(line, bounds);
      }
    }

    if (options?.trackCounts) {
      const lineType = (line as any).data?.type;
      if (lineType === 'horizontal') {
        this.rowCount++;
      } else if (lineType === 'vertical') {
        this.colCount++;
      }
    }

    return line;
  }

  private getScaledGridBounds(): { left: number; top: number; width: number; height: number; scaleX: number; scaleY: number } | null {
    if (!this.gridRect) {
      return null;
    }

    const scaleX = this.gridRect.scaleX || 1;
    const scaleY = this.gridRect.scaleY || 1;

    return {
      left: this.gridRect.left || 0,
      top: this.gridRect.top || 0,
      width: (this.gridRect.width || 0) * scaleX,
      height: (this.gridRect.height || 0) * scaleY,
      scaleX,
      scaleY
    };
  }

  private computeRatioWithinBounds(
    type: 'horizontal' | 'vertical',
    position: number,
    bounds: { left: number; top: number; width: number; height: number }
  ): number {
    const rectStart = type === 'horizontal' ? bounds.top : bounds.left;
    const rectSize = type === 'horizontal' ? bounds.height : bounds.width;
    if (!rectSize) {
      return 0;
    }
    const unclamped = (position - rectStart) / rectSize;
    return Math.min(Math.max(unclamped, 0), 1);
  }

  private applyLineRatio(line: Line, ratio: number): void {
    const data = (line as any).data || {};
    (line as any).data = { ...data, ratioWithinRect: ratio };
  }

  private updateLineRatioFromPosition(
    line: Line,
    bounds: { left: number; top: number; width: number; height: number }
  ): void {
    const data = (line as any).data;
    const type = data?.type;
    if (type !== 'horizontal' && type !== 'vertical') {
      return;
    }
    const position = type === 'horizontal' ? (line.top || 0) : (line.left || 0);
    const ratio = this.computeRatioWithinBounds(type, position, bounds);
    this.applyLineRatio(line, ratio);
  }

  private enableCustomGridResizeMode(): void {
    if (!this.gridRect || !this.fabricCanvas) return;

    // Disable Fabric's default selection box when grid is active
    this.fabricCanvas.selection = false;

    this.gridRect.set({
      selectable: false,
      evented: false,
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      lockScalingX: true,
      lockScalingY: true,
      hoverCursor: 'default'
    });
    this.gridRect.setCoords();
    this.createGridEdgeHandles();
  }

  private createGridEdgeHandles(): void {
    if (!this.fabricCanvas || !this.gridRect) return;

    this.removeGridEdgeHandles();

    const handleConfigs: Array<{
      type: GridHandleType;
      cursor: string;
      lockMovementX: boolean;
      lockMovementY: boolean;
    }> = [
      { type: 'left', cursor: 'ew-resize', lockMovementX: false, lockMovementY: true },
      { type: 'right', cursor: 'ew-resize', lockMovementX: false, lockMovementY: true },
      { type: 'top', cursor: 'ns-resize', lockMovementX: true, lockMovementY: false },
      { type: 'bottom', cursor: 'ns-resize', lockMovementX: true, lockMovementY: false }
    ];

    handleConfigs.forEach(config => {
      const handle = new Rect({
        width: 1,
        height: 1,
        fill: 'rgba(0, 255, 0, 0.01)',
        stroke: 'rgba(0, 0, 0, 0)',
        strokeWidth: 0,
        originX: 'center',
        originY: 'center',
        rx: 2,
        ry: 2,
        selectable: true,
        evented: true,
        hasBorders: false,
        hasControls: false,
        lockScalingX: true,
        lockScalingY: true,
        hoverCursor: config.cursor,
        lockMovementX: config.lockMovementX,
        lockMovementY: config.lockMovementY,
        data: { type: 'grid-handle', handleType: config.type }
      } as any);

      handle.on('moving', () => this.onGridHandleMoving(config.type, handle));
      handle.on('mousedown', () => this.fabricCanvas!.bringObjectToFront(handle));

      this.gridEdgeHandles[config.type] = handle;
      this.fabricCanvas.add(handle);
    });

    this.updateGridEdgeHandlePositions();
  }

  private removeGridEdgeHandles(): void {
    if (!this.fabricCanvas) {
      this.gridEdgeHandles = {};
      return;
    }

    Object.values(this.gridEdgeHandles).forEach(handle => {
      if (handle) {
        this.fabricCanvas!.remove(handle);
      }
    });
    this.gridEdgeHandles = {};
  }

  private onGridHandleMoving(handleType: GridHandleType, handle: Rect): void {
    if (!this.gridRect || !this.fabricCanvas) return;

    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      return;
    }
    const { left: currentLeft, top: currentTop, width: currentWidth, height: currentHeight, scaleX, scaleY } = bounds;
    const canvasWidth = this.fabricCanvas.getWidth();
    const canvasHeight = this.fabricCanvas.getHeight();
    const currentRight = currentLeft + currentWidth;
    const currentBottom = currentTop + currentHeight;

    switch (handleType) {
      case 'left': {
        let newLeft = Math.min(handle.left ?? currentLeft, currentRight - this.GRID_MIN_WIDTH);
        newLeft = Math.max(0, newLeft);
        const newWidth = currentRight - newLeft;
        this.gridRect.set({ left: newLeft, width: newWidth / scaleX });
        break;
      }
      case 'right': {
        let newRight = Math.max(handle.left ?? currentRight, currentLeft + this.GRID_MIN_WIDTH);
        newRight = Math.min(canvasWidth, newRight);
        const newWidth = newRight - currentLeft;
        this.gridRect.set({ width: newWidth / scaleX });
        break;
      }
      case 'top': {
        let newTop = Math.min(handle.top ?? currentTop, currentBottom - this.GRID_MIN_HEIGHT);
        newTop = Math.max(0, newTop);
        const newHeight = currentBottom - newTop;
        this.gridRect.set({ top: newTop, height: newHeight / scaleY });
        break;
      }
      case 'bottom': {
        let newBottom = Math.max(handle.top ?? currentBottom, currentTop + this.GRID_MIN_HEIGHT);
        newBottom = Math.min(canvasHeight, newBottom);
        const newHeight = newBottom - currentTop;
        this.gridRect.set({ height: newHeight / scaleY });
        break;
      }
    }

    this.gridRect.setCoords();
    this.updateGridLinesPosition();
  }

  private updateGridEdgeHandlePositions(): void {
    if (!this.gridRect || !this.fabricCanvas) return;

    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      return;
    }
    const { left, top, width, height } = bounds;

    const thickness = this.GRID_HANDLE_THICKNESS;
    const handleBounds: Record<GridHandleType, { x: number; y: number; width: number; height: number }> = {
      left: { x: left, y: top + height / 2, width: thickness, height },
      right: { x: left + width, y: top + height / 2, width: thickness, height },
      top: { x: left + width / 2, y: top, width, height: thickness },
      bottom: { x: left + width / 2, y: top + height, width, height: thickness }
    };

    (Object.entries(this.gridEdgeHandles) as Array<[GridHandleType, Rect | undefined]>).forEach(([key, handle]) => {
      const bound = handleBounds[key];
      if (!handle || !bound) return;

      handle.set({
        left: bound.x,
        top: bound.y,
        width: bound.width,
        height: bound.height
      });
      handle.setCoords();
      this.fabricCanvas!.bringObjectToFront(handle);
    });
  }

  private updateGridLinesPosition(): void {
    if (!this.gridRect || !this.fabricCanvas) return;
    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      return;
    }
    const { left, top, width, height } = bounds;

    const tolerance = 5; // 5px tolerance for boundary checking

    const horizontalLines = this.gridLines.filter(line => line.get('lockMovementX'));
    const linesToRemove: Line[] = [];

    horizontalLines.forEach((line) => {
      const currentY = line.top!;
      if (currentY < (top - tolerance) || currentY > (top + height + tolerance)) {
        linesToRemove.push(line);
      } else {
        this.updateLineGeometry(line, 'horizontal', bounds, currentY);
      }
    });

    const verticalLines = this.gridLines.filter(line => line.get('lockMovementY'));

    verticalLines.forEach((line) => {
      const currentX = line.left!;
      if (currentX < (left - tolerance) || currentX > (left + width + tolerance)) {
        linesToRemove.push(line);
      } else {
        this.updateLineGeometry(line, 'vertical', bounds, currentX);
      }
    });

    if (linesToRemove.length > 0) {
      linesToRemove.forEach(line => {
        this.fabricCanvas!.remove(line);
        const index = this.gridLines.indexOf(line);
        if (index > -1) {
          this.gridLines.splice(index, 1);
        }
      });

      const remainingHorizontalLines = this.gridLines.filter(line => line.get('lockMovementX')).length;
      const remainingVerticalLines = this.gridLines.filter(line => line.get('lockMovementY')).length;

      this.rowCount = remainingHorizontalLines + 1;
      this.colCount = remainingVerticalLines + 1;
    }

    this.updateGridEdgeHandlePositions();
    this.fabricCanvas.renderAll();
  }

  private getLineInteractionProps(type: 'horizontal' | 'vertical'): Record<string, any> {
    const baseProps = {
      selectable: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      hasControls: false,
      hasBorders: false,
      evented: true
    };

    if (type === 'horizontal') {
      return {
        ...baseProps,
        lockMovementX: true,
        lockMovementY: false,
        hoverCursor: 'ns-resize',
        moveCursor: 'ns-resize'
      };
    }

    return {
      ...baseProps,
      lockMovementX: false,
      lockMovementY: true,
      hoverCursor: 'ew-resize',
      moveCursor: 'ew-resize'
    };
  }

  private updateLineGeometry(line: Line, type: 'horizontal' | 'vertical', bounds: { left: number; top: number; width: number; height: number }, position: number): void {
    if (type === 'horizontal') {
      line.set({
        x1: bounds.left,
        y1: position,
        x2: bounds.left + bounds.width,
        y2: position,
        top: position,
        left: bounds.left,
        ...this.getLineInteractionProps('horizontal')
      });
    } else {
      line.set({
        x1: position,
        y1: bounds.top,
        x2: position,
        y2: bounds.top + bounds.height,
        left: position,
        top: bounds.top,
        ...this.getLineInteractionProps('vertical')
      });
    }

    line.setCoords();
    this.updateLineRatioFromPosition(line, bounds);
  }

  // ===================== Grid data extraction

  async extractGridData(
    pdfCanvas: HTMLCanvasElement,
    pageNumber: number,
    pageKey?: string,
    customTextItems?: any[]
  ): Promise<GridData> {
    if (!this.gridRect || !this.fabricCanvas) {
      throw new Error('No grid defined');
    }

    const textItems = Array.isArray(customTextItems)
      ? customTextItems
      : this.getTextItemsForPage(pdfCanvas, pageNumber, pageKey, 'Extract Grid');

    if (textItems.length === 0) {
      throw new Error(`No text detected in the selected area. Grid extraction aborted.`);
    }

    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      throw new Error('Cannot extract grid data: missing grid bounds');
    }
    const { left, top, width, height } = bounds;

    // Scale factors between Fabric (Display) and PDF (Internal)
    const scaleX_Internal = pdfCanvas.width / this.fabricCanvas.getWidth();
    const scaleY_Internal = pdfCanvas.height / this.fabricCanvas.getHeight();

    const effectivePageKey = pageKey || this.activePageKey || null;

    this.autoGenerateGridStructure(
      textItems,
      { left, top, width, height },
      scaleX_Internal,
      scaleY_Internal
    );

    // GET GRID BOUNDARIES FROM CANVAS
    const horizontalLines = this.gridLines
      .filter(line => line.get('lockMovementX'))
      .map(line => line.top!)
      .sort((a, b) => a - b);

    const verticalLines = this.gridLines
      .filter(line => line.get('lockMovementY'))
      .map(line => line.left!)
      .sort((a, b) => a - b);

    const rowBoundaries = [top, ...horizontalLines, top + height];
    const colBoundaries = [left, ...verticalLines, left + width];

    const cells: GridCell[] = [];

    // CRITICAL FIX: Calculate actual row/col counts from boundaries
    const actualRowCount = rowBoundaries.length - 1;
    const actualColCount = colBoundaries.length - 1;

    // Validate and sync cached counts
    if (actualRowCount !== this.rowCount || actualColCount !== this.colCount) {
      this.logger.warn(`[Extract Grid] ⚠️ Count mismatch detected! Cached: ${this.rowCount}x${this.colCount}, Actual from boundaries: ${actualRowCount}x${actualColCount}`);
      this.rowCount = actualRowCount;
      this.colCount = actualColCount;
    }

    const scaleX = pdfCanvas.width / this.fabricCanvas.getWidth();
    const scaleY = pdfCanvas.height / this.fabricCanvas.getHeight();

    // Extract text from cached OCR based on current grid positions
    for (let row = 0; row < actualRowCount; row++) {
      for (let col = 0; col < actualColCount; col++) {
        const cellLeft = colBoundaries[col];
        const cellTop = rowBoundaries[row];
        const cellWidth = colBoundaries[col + 1] - cellLeft;
        const cellHeight = rowBoundaries[row + 1] - cellTop;

        const scaledLeft = cellLeft * scaleX;
        const scaledTop = cellTop * scaleY;
        const scaledWidth = cellWidth * scaleX;
        const scaledHeight = cellHeight * scaleY;

        // Filter textItems using overlap detection (more inclusive than center-point)
        const cellTextItems = textItems.filter((item: any) => {
          const itemRight = item.x + item.width;
          const itemBottom = item.y + item.height;
          const cellRight = scaledLeft + scaledWidth;
          const cellBottom = scaledTop + scaledHeight;

          const overlapWidth = Math.min(itemRight, cellRight) - Math.max(item.x, scaledLeft);
          const overlapHeight = Math.min(itemBottom, cellBottom) - Math.max(item.y, scaledTop);
          if (overlapWidth <= 0 || overlapHeight <= 0) {
            return false;
          }

          const overlapArea = overlapWidth * overlapHeight;
          const itemArea = item.width * item.height || 1;
          const overlapRatio = overlapArea / itemArea;
          const exceedsOverlapThreshold = overlapRatio >= this.CELL_ITEM_OVERLAP_THRESHOLD;

          // For data rows (row >= 1), exclude items from header row
          // Header text should only appear in row 0
          if (row >= 1 && rowBoundaries.length > 1) {
            const firstDataRowTop = rowBoundaries[1] * scaleY;
            // Exclude items that start above the first data row boundary (header text)
            if (item.y < firstDataRowTop) {
              return false;
            }
          }

          return exceedsOverlapThreshold;
        });

        // Sort by Y first (top to bottom), then X (left to right)
        const sortedItems = cellTextItems.sort((a: any, b: any) => {
          const yDiff = a.y - b.y;
          if (Math.abs(yDiff) > 10) return yDiff; // Different lines
          return a.x - b.x; // Same line, sort by X
        });

        // Merge text items that are close together horizontally (same line)
        const mergedText: string[] = [];
        let currentLine: any[] = [];
        let lastY = -1;

        sortedItems.forEach((item: any) => {
          if (lastY === -1 || Math.abs(item.y - lastY) < 10) {
            // Same line
            currentLine.push(item);
            lastY = item.y;
          } else {
            // New line - merge current line and start new one
            if (currentLine.length > 0) {
              const lineText = currentLine
                .sort((a, b) => a.x - b.x)
                .map(i => i.text)
                .join(' ');
              mergedText.push(lineText);
            }
            currentLine = [item];
            lastY = item.y;
          }
        });

        // Don't forget the last line
        if (currentLine.length > 0) {
          const lineText = currentLine
            .sort((a, b) => a.x - b.x)
            .map(i => i.text)
            .join(' ');
          mergedText.push(lineText);
        }

        const text = this.sanitizeExtractedText(mergedText.join(' ').trim());

        let confidence = 0;
        if (sortedItems.length > 0) {
          const totalConfidence = sortedItems.reduce((sum: number, item: any) => {
            const value = typeof item.confidence === 'number' ? item.confidence : 100;
            return sum + value;
          }, 0);
          confidence = Math.round(totalConfidence / sortedItems.length);
        } else {
          confidence = text ? 85 : 0;
        }

        const hasLowConfidence = sortedItems.some(
          (item: any) => typeof item.confidence === 'number' && item.confidence < this.LOW_CONFIDENCE_THRESHOLD
        );

        // Log diagnostic info for empty cells
        if (!text && row < 3 && col < 3) {
          this.logger.warn(`[Extract Grid] Cell [${row},${col}] is empty. Available items in vicinity: ${textItems.filter((item: any) => {
            const distance = Math.sqrt(Math.pow(item.x - scaledLeft, 2) + Math.pow(item.y - scaledTop, 2));
            return distance < 100;
          }).length}`);
        }

        cells.push({
          row,
          col,
          text,
          confidence,
          hasLowConfidence,
          ignored: false,
          bounds: {
            left: scaledLeft,
            top: scaledTop,
            width: scaledWidth,
            height: scaledHeight
          },
          location: effectivePageKey ? { pageKey: effectivePageKey, row, col } : undefined
        });
      }
    }

    // Validate if first row should be marked as header (ignored)
    const isFirstRowHeader = this.validateFirstRowAsHeader(cells);
    if (isFirstRowHeader) {
      // Mark first row as ignored (header row)
      cells.filter(c => c.row === 0).forEach(c => c.ignored = true);
      this.logger.debug('[Extract Grid] First row marked as header (ignored)');
    } else {
      // First row is normal data, not a header
      this.logger.debug('[Extract Grid] First row treated as normal data row (not a header)');
    }

    return {
      rows: this.rowCount,
      cols: this.colCount,
      cells
    };
  }

  private validateFirstRowAsHeader(cells: GridCell[]): boolean {
    if (!cells || cells.length === 0) return false;

    const headerKeywords = this.HEADER_KEYWORDS;

    // Get first row cells
    const firstRowCells = cells.filter(c => c.row === 0);
    if (firstRowCells.length === 0) return false;

    // Count how many cells match header keywords
    let matchCount = 0;
    firstRowCells.forEach(cell => {
      const cellText = (cell.text || '').toLowerCase().trim();
      if (cellText) {
        const hasMatch = headerKeywords.some(keyword => cellText.includes(keyword.toLowerCase()));
        if (hasMatch) {
          matchCount++;
          this.logger.debug(`[Header Validation] Cell [0,${cell.col}] "${cell.text}" matches header keyword`);
        }
      }
    });

    const isHeader = matchCount >= 2; // Require at least 2 matches
    this.logger.debug(`[Header Validation] First row has ${matchCount} header keyword matches. Is header: ${isHeader}`);

    return isHeader;
  }

  private sanitizeExtractedText(rawText: string): string {
    if (!rawText) {
      return '';
    }

    // Allow numbers, letters, whitespace, dash, slash, comma, decimal point and common currency symbols.
    const disallowedCharacters = /[^0-9a-zA-Z\s\/,.\-\$€£¥₹₩₽₺₨₦₫฿₴₱₲₵₡₸¢]/g;
    return rawText
      .replace(disallowedCharacters, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // ===================== OCR data helpers

  getTextItemsForPage(
    pdfCanvas: HTMLCanvasElement,
    pageNumber: number,
    pageKey?: string,
    logContext = 'Text Extraction'
  ): any[] {
    const cacheKey = pageKey || 'pdf-page-fallback';
    const cachedPage = this.getPageOcr(cacheKey);

    if (cachedPage) {
      const scaledItems = this.scaleNormalizedOcrItems(cachedPage.items, pdfCanvas);
      this.logger.debug(`[${logContext}] Loaded ${scaledItems.length} text items from cache.`);
      return scaledItems;
    }

    this.logger.debug(`[${logContext}] No local cache for ${cacheKey}. Checking Tesseract Source...`);

    try {
      const jsonData = this.getTesseractData();
      if (!jsonData?.pages?.length) {
        throw new Error(`[${logContext}] No page detected from Tesseract cache.`);
      }

      const pageData = jsonData.pages.find((page: any) => page.pageNumber === pageNumber);
      if (!pageData) {
        throw new Error(`[${logContext}] Page ${pageNumber} not found in Tesseract cache.`);
      }

      const hasWords = Array.isArray(pageData.words) && pageData.words.length > 0;
      const hasCharacters = Array.isArray((pageData as any).characters) && (pageData as any).characters.length > 0;

      if (!hasWords && !hasCharacters) {
        throw new Error(`[${logContext}] No text detected from cache for page ${pageNumber}.`);
      }

      const sourceItems = hasWords ? pageData.words! : (pageData as any).characters;
      const pageWidth = pageData.width || 1;
      const pageHeight = pageData.height || 1;

      const normalizedItems: NormalizedOcrItem[] = sourceItems.map((word: any) => {
        const textValue = word.text ?? word.char ?? '';
        const x0 = word.x0 || 0;
        const x1 = word.x1 || 0;
        const top = word.top || 0;
        const bottom = word.bottom || 0;

        const confidence = typeof word.confidence === 'number' ? word.confidence : undefined;
        const width = Math.max(x1 - x0, 0);
        const height = Math.max(bottom - top, 0);

        return {
          text: textValue,
          xRatio: x0 / pageWidth,
          yRatio: top / pageHeight,
          widthRatio: width / pageWidth,
          heightRatio: height / pageHeight,
          confidence
        };
      });

      this.cachePageOcr(cacheKey, { items: normalizedItems });

      const scaledItems = this.scaleNormalizedOcrItems(normalizedItems, pdfCanvas);
      this.logger.debug(`[${logContext}] Loaded ${scaledItems.length} text items from source.`);

      return scaledItems;
    } catch (error) {
      this.logger.error(`[${logContext}] Tesseract mapping failed`, error);
      throw new Error(`${logContext} Failed: ${error.message || error}`);
    }
  }

  // Make this an explicit
  cacheTesseractData(data: TesseractOutput | null): void {
    this.tesseractCache = data;
    console.log('GridExtractionService: Cache updated', this.tesseractCache);
  }

  getTesseractData(): TesseractOutput | null {
    return this.tesseractCache;
  }

  // Store full-page OCR result
  cachePageOcr(pageKey: string, pageData: CachedOcrPage): void {
    this.pageOcrCache.set(pageKey, pageData);
  }

  // Retrieve full-page OCR result
  getPageOcr(pageKey: string): CachedOcrPage | null {
    return this.pageOcrCache.get(pageKey) || null;
  }

  private scaleNormalizedOcrItems(items: NormalizedOcrItem[], pdfCanvas: HTMLCanvasElement): any[] {
    const canvasWidth = pdfCanvas.width || 1;
    const canvasHeight = pdfCanvas.height || 1;

    return items.map(item => ({
      text: item.text,
      x: item.xRatio * canvasWidth,
      y: item.yRatio * canvasHeight,
      width: item.widthRatio * canvasWidth,
      height: item.heightRatio * canvasHeight,
      confidence: item.confidence
    }));
  }

  // ============== General

  getGridBoundsForPdfCanvas(pdfCanvas: HTMLCanvasElement): { left: number; top: number; width: number; height: number } | null {
    if (!this.fabricCanvas) {
      this.logger.warn('[Grid Bounds] Fabric canvas not initialized.');
      return null;
    }

    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      this.logger.warn('[Grid Bounds] Grid rectangle not initialized.');
      return null;
    }

    const canvasWidth = this.fabricCanvas.getWidth() || 1;
    const canvasHeight = this.fabricCanvas.getHeight() || 1;

    const scaleX = pdfCanvas.width / canvasWidth;
    const scaleY = pdfCanvas.height / canvasHeight;

    return {
      left: bounds.left * scaleX,
      top: bounds.top * scaleY,
      width: bounds.width * scaleX,
      height: bounds.height * scaleY
    };
  }

  setHighlightedCell(location: GridHighlightLocation | null): void {
    this.activeHighlight = location ? { ...location } : null;
    this.updateActivePageHighlightState();
  }

  getActiveHighlight(): GridHighlightLocation | null {
    return this.activeHighlight ? { ...this.activeHighlight } : null;
  }

  private updateActivePageHighlightState(): void {
    if (!this.activePageKey) {
      return;
    }

    const currentState = this.pageGridStates.get(this.activePageKey);
    if (!currentState) {
      return;
    }

    if (this.activeHighlight && this.activePageKey && this.activeHighlight.pageKey === this.activePageKey) {
      currentState.highlight = { ...this.activeHighlight };
    } else {
      currentState.highlight = null;
    }

    this.pageGridStates.set(this.activePageKey, currentState);
  }

  getCellDisplayBounds(rowIndex: number, colIndex: number): { left: number; top: number; width: number; height: number } | null {
    if (!this.gridRect) {
      this.logger.warn('[Grid Bounds] Grid rectangle not initialized.');
      return null;
    }

    const bounds = this.getScaledGridBounds();
    if (!bounds) {
      this.logger.warn('[Grid Bounds] Unable to compute scaled grid bounds.');
      return null;
    }

    const horizontalLines = this.gridLines
      .filter(line => line.get('lockMovementX'))
      .map(line => line.top!)
      .sort((a, b) => a - b);

    const verticalLines = this.gridLines
      .filter(line => line.get('lockMovementY'))
      .map(line => line.left!)
      .sort((a, b) => a - b);

    const rowBoundaries = [bounds.top, ...horizontalLines, bounds.top + bounds.height];
    const colBoundaries = [bounds.left, ...verticalLines, bounds.left + bounds.width];

    const lastRowIndex = rowBoundaries.length - 2;
    const lastColIndex = colBoundaries.length - 2;

    if (rowIndex < 0 || rowIndex > lastRowIndex || colIndex < 0 || colIndex > lastColIndex) {
      this.logger.warn(`[Grid Bounds] Requested cell [${rowIndex},${colIndex}] outside current grid.`);
      return null;
    }

    const cellLeft = colBoundaries[colIndex];
    const cellTop = rowBoundaries[rowIndex];
    const cellWidth = colBoundaries[colIndex + 1] - cellLeft;
    const cellHeight = rowBoundaries[rowIndex + 1] - cellTop;

    return {
      left: cellLeft,
      top: cellTop,
      width: cellWidth,
      height: cellHeight
    };
  }

  clearGrid(): void {
    if (!this.fabricCanvas) return;

    this.clearCanvasObjects();
    if (this.activePageKey) {
      this.pageGridStates.delete(this.activePageKey);
    }
    this.activeHighlight = null;

    this.fabricCanvas.renderAll();
  }

  hasGrid(): boolean {
    return this.gridRect !== null;
  }

  hasFabricCanvas(): boolean {
    return this.fabricCanvas !== null;
  }

  saveAndCleanUpCanvas(): void {
    this.saveActivePageState();
    this.clearCanvasObjects();

    if (this.fabricCanvas) {
      this.fabricCanvas.dispose();  // Fabric JS tear down
      this.fabricCanvas = null;
    }

    this.activePageKey = null;
  }

  private clearCanvasObjects(resetCounts = true): void {
    if (!this.fabricCanvas) return;

    // Re-enable Fabric selection when no grid objects remain
    this.fabricCanvas.selection = true;

    if (this.gridRect) {
      this.fabricCanvas.remove(this.gridRect);
      this.gridRect = null;
    }

    this.gridLines.forEach(line => this.fabricCanvas!.remove(line));
    this.gridLines = [];
    this.removeGridEdgeHandles();

    if (resetCounts) {
      this.rowCount = 1;
      this.colCount = 1;
    }
  }

  private requireGridContext(action: string): boolean {
    if (!this.fabricCanvas || !this.gridRect) {
      this.logger.error(`Cannot ${action}: no grid defined`);
      return false;
    }
    return true;
  }

}
